import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SearchInputComponent } from '../../../inputs/search-input/search-input.component';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { IOrganization, ITag } from '../../../../types/contactModule';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { replaceUnderscores } from '../../../../../utils/utils';

export type GroupPickerKind = 'organization' | 'tag';
type GroupItem = IOrganization | ITag;

const PAGE_SIZE = 10;

/**
 * Searchable, paged checklist of organizations or tags, used by every
 * "assign to…" modal in the contacts area. It loads its own pages (no shared
 * list state, so the contact table behind the modal never flickers) and
 * reports the full selection, across pages and searches, on every change.
 */
@Component({
  selector: 'bk-group-picker',
  standalone: true,
  imports: [SearchInputComponent, FormsModule],
  templateUrl: './group-picker.component.html',
  styleUrl: './group-picker.component.css',
})
export class GroupPickerComponent implements OnInit, OnChanges {
  private contactsService = inject(ContactsService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) kind: GroupPickerKind = 'organization';
  /** What is ticked when the picker opens. */
  @Input() selected: GroupItem[] | null | undefined = [];
  /** Offer "Create tag" when a search finds no exact match (tags only). */
  @Input() allowCreate = false;
  @Output() selectedChange = new EventEmitter<GroupItem[]>();

  items: GroupItem[] = [];
  page = 0;
  totalPages = 1;
  totalElements = 0;
  loading = false;
  loadError = '';
  term = '';

  // Create-tag flow
  showCreate = false;
  newName = '';
  newDescription = '';
  creating = false;
  createError = '';
  createdName = '';

  private chosen = new Map<string, GroupItem>();
  private request?: Subscription;

  get noun(): string {
    return this.kind === 'tag' ? 'tag' : 'organization';
  }

  /** Always-present status line: loading the first page, then a created tag. */
  get statusText(): string {
    if (this.createdName) return `Created “${this.createdName}” and selected it.`;
    if (this.loading && !this.items.length && !this.loadError) return `Loading ${this.nounPlural}`;
    return '';
  }

  get nounPlural(): string {
    return this.kind === 'tag' ? 'tags' : 'organizations';
  }

  get selectedCount(): number {
    return this.chosen.size;
  }

  get selectedItems(): GroupItem[] {
    return Array.from(this.chosen.values());
  }

  get rangeLabel(): string {
    if (!this.totalElements) return '';
    const from = this.page * PAGE_SIZE + 1;
    const to = Math.min((this.page + 1) * PAGE_SIZE, this.totalElements);
    return `${formatCount(from)}–${formatCount(to)} of ${formatCount(this.totalElements)}`;
  }

  get allOnPageChosen(): boolean {
    return this.items.length > 0 && this.items.every((item) => this.isChosen(item));
  }

  get someOnPageChosen(): boolean {
    return !this.allOnPageChosen && this.items.some((item) => this.isChosen(item));
  }

  /** A search with no exact name match can become a new tag. */
  get canOfferCreate(): boolean {
    const term = this.term.trim().toLowerCase();
    if (!this.allowCreate || this.kind !== 'tag' || !term || this.loading) return false;
    return !this.items.some((item) => (item.name || '').toLowerCase() === term);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected']) {
      this.chosen = new Map(
        (this.selected ?? []).filter(Boolean).map((item) => [this.keyOf(item), item] as [string, GroupItem])
      );
    }
  }

  ngOnInit(): void {
    this.load();
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  keyOf(item: GroupItem): string {
    return String(item?.id ?? item?.uuid ?? item?.name ?? '');
  }

  isChosen(item: GroupItem): boolean {
    return this.chosen.has(this.keyOf(item));
  }

  toggle(item: GroupItem): void {
    const key = this.keyOf(item);
    if (this.chosen.has(key)) this.chosen.delete(key);
    else this.chosen.set(key, item);
    this.emit();
  }

  toggleAllOnPage(): void {
    const selectAll = !this.allOnPageChosen;
    for (const item of this.items) {
      const key = this.keyOf(item);
      if (selectAll) this.chosen.set(key, item);
      else this.chosen.delete(key);
    }
    this.emit();
  }

  clearSelection(): void {
    this.chosen.clear();
    this.emit();
  }

  onSearch(term: string): void {
    this.term = term ?? '';
    this.page = 0;
    this.load();
  }

  goTo(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page) return;
    this.page = page;
    this.load();
  }

  retry(): void {
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    const params: Record<string, any> = { page: this.page, size: PAGE_SIZE, sort: 'name,asc' };
    const term = this.term.trim();
    if (term) params['name.contains'] = term;

    const source =
      this.kind === 'tag'
        ? this.contactsService.searchTags(params)
        : this.contactsService.searchOrganizations(params);

    this.request = source
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ items, itemPagination }) => {
          this.items = items ?? [];
          this.totalElements = Number(itemPagination?.totalElements ?? this.items.length) || 0;
          this.totalPages = Math.max(1, Number(itemPagination?.totalPages ?? 1) || 1);
        },
        error: () => {
          this.items = [];
          this.loadError = `Could not load ${this.nounPlural}. Check your connection and try again.`;
        },
      });
  }

  // ── Create a tag without leaving the picker ──
  openCreate(): void {
    this.showCreate = true;
    this.createError = '';
    this.createdName = '';
    this.newName = this.term.trim();
  }

  cancelCreate(): void {
    this.showCreate = false;
    this.createError = '';
    this.newName = '';
    this.newDescription = '';
  }

  createTag(): void {
    const name = (this.newName || '').trim();
    const description = (this.newDescription || '').trim();
    if (!name) {
      this.createError = 'Enter a tag name.';
      return;
    }
    const existing = this.items.find((item) => (item.name || '').toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!this.isChosen(existing)) this.toggle(existing);
      this.createdName = existing.name || name;
      this.cancelCreate();
      return;
    }

    this.creating = true;
    this.createError = '';
    this.contactsService
      .createTag({ name, description })
      .pipe(
        finalize(() => {
          this.creating = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (created: ITag) => {
          const tag: ITag = created?.id ? created : ({ name, description } as ITag);
          if (created?.id) {
            this.chosen.set(this.keyOf(tag), tag);
            this.items = [tag, ...this.items.filter((item) => this.keyOf(item) !== this.keyOf(tag))];
            this.emit();
          }
          this.createdName = tag.name || name;
          this.showCreate = false;
          this.newName = '';
          this.newDescription = '';
        },
        error: () => {
          this.createError = 'The tag was not created. Try a different name.';
        },
      });
  }

  description(item: GroupItem): string {
    return (item?.description || '').toString().trim();
  }

  industry(item: GroupItem): string {
    const value = (item as IOrganization)?.industry;
    if (!value) return '—';
    const words = replaceUnderscores(value).toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  country(item: GroupItem): string {
    return (item as IOrganization)?.country || '—';
  }

  created(item: GroupItem): string {
    return formatWhen(item?.createdAt ?? null);
  }

  private emit(): void {
    this.selectedChange.emit(this.selectedItems);
    this.cdr.markForCheck();
  }
}
